import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, DownloadSimple, GearSix } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, API, downloadFile, photoHref } from "@/lib/api";

export const LABEL_KINDS = ["Install", "Template"];

export const LABEL_PRINT_CSS = `
  @media print {
    @page { size: 4in 6in; margin: 0; }
    html, body { background: #fff !important; }
    body * { visibility: hidden !important; }
    /* Radix centres the dialog with a CSS transform, which makes the label's
       position:fixed resolve against the dialog box instead of the page and
       prints a blank sheet. Neutralise the transform and collapse the dialog so
       the 4x6 label maps cleanly onto the printed page. */
    [data-testid="label-dialog"] {
      position: static !important;
      transform: none !important;
      inset: auto !important;
      overflow: visible !important;
      padding: 0 !important; margin: 0 !important; border: 0 !important;
    }
    .label-sheet, .label-sheet * { visibility: visible !important; }
    .label-sheet {
      position: absolute !important; top: 0 !important; left: 0 !important;
      display: flex !important; page: label;
      margin: 0 !important; box-shadow: none !important;
    }
    /* This stylesheet only exists while a 4x6 label dialog is open, so suppress any
       other page-level print surface behind the dialog (e.g. a reservation's own
       label preview) so ONLY the open label prints. */
    #print-area:not([data-testid="sink-label-sheet"]):not([data-testid="install-label-sheet"]) {
      display: none !important; visibility: hidden !important;
    }
    .no-print { display: none !important; }
  }
`;

const Field = ({ label, value, big, testId }) =>
  value ? (
    <div className="mb-2" data-testid={testId}>
      <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-500">{label}</div>
      <div className={`font-bold leading-tight text-black ${big ? "text-[15pt]" : "text-[11pt]"}`}>
        {value}
      </div>
    </div>
  ) : null;

export const LabelSheet = ({ d }) => {
  const on = d.fields || {};
  return (
    <div
      id="print-area"
      className="label-sheet print-label relative mx-auto flex flex-col bg-white p-[0.25in] text-black shadow-2xl"
      style={{ width: "4in", height: "6in" }}
      data-testid="install-label-sheet"
    >
      <div className="mb-1 flex items-end justify-between border-b-2 border-black pb-1">
        <div>
          {on.logo && d.has_logo ? (
            <img src={`${API}/public/logo`} alt="" className="max-h-[0.5in] max-w-[1.6in] object-contain" />
          ) : (
            <div className="text-[10pt] font-bold">{d.company_name}</div>
          )}
        </div>
        <div className="text-right text-[9pt] font-black uppercase tracking-widest">
          {d.kind}{on.job_code && d.job_code ? ` · ${d.job_code}` : ""}
        </div>
      </div>
      {on.install_date && d.install_date && (
        <div className="mb-2 -mx-[0.25in] bg-black px-[0.25in] py-1.5 text-white" data-testid="label-install-date">
          <div className="text-[7pt] uppercase tracking-[0.18em] text-neutral-400">{d.kind} date</div>
          <div className="text-[17pt] font-black leading-tight">{d.install_date}</div>
          {on.window && d.window && (
            <div className="mt-0.5 text-[9pt] font-bold uppercase tracking-widest text-amber-300" data-testid="label-window">
              Arrival window · {d.window}
            </div>
          )}
        </div>
      )}
      {on.customer_name && <Field label="Customer" value={d.customer_name} big testId="label-customer" />}
      {on.phone && <Field label="Phone" value={d.phone} testId="label-phone" />}
      {on.address && <Field label="Job address" value={d.address} big testId="label-address" />}
      {on.access_notes && d.access_notes && (
        <div className="mb-2 rounded border-2 border-amber-400 bg-amber-100 px-2 py-1" data-testid="label-access-notes">
          <div className="text-[7pt] uppercase tracking-[0.18em] text-amber-800">Gate / entry</div>
          <div className="text-[10pt] font-bold leading-tight text-black">{d.access_notes.slice(0, 140)}</div>
        </div>
      )}
      {on.crew && <Field label="Crew" value={d.crew} testId="label-crew" />}
      {on.sinks && <Field label="Sinks" value={(d.sinks || []).join("; ")} testId="label-sinks" />}
      {on.material && <Field label="Material" value={d.material} testId="label-material" />}
      {on.pieces && d.pieces > 0 && <Field label="Pieces" value={String(d.pieces)} testId="label-pieces" />}
      {on.notes && <Field label="Notes" value={(d.notes || "").slice(0, 160)} testId="label-notes" />}
      {on.sink_photo && d.sink_photo && (
        <div className="absolute bottom-[0.25in] left-[0.25in]" data-testid="label-sink-photo">
          <img
            src={photoHref(d.sink_photo)}
            alt="Reserved sink"
            className="h-[1.1in] w-[1.1in] rounded border border-neutral-300 object-contain"
          />
          <div className="mt-0.5 text-[6pt] uppercase tracking-widest text-neutral-600">Reserved sink</div>
        </div>
      )}
      {on.qr && d.maps_url && (
        <div className="absolute bottom-[0.25in] right-[0.25in] text-center" data-testid="label-qr">
          <QRCodeSVG value={d.maps_url} size={120} level="M" />
          <div className="mt-0.5 text-[6pt] uppercase tracking-widest text-neutral-600">Scan for directions</div>
        </div>
      )}
    </div>
  );
};

export const useLabelData = (jobId, kind, enabled) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!enabled || !jobId) return;
    setD(null);
    setErr("");
    api
      .get(`/jobs/${jobId}/install-label`, { params: { kind } })
      .then(({ data }) => setD(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load label"));
  }, [jobId, kind, enabled]);
  return { d, err };
};

export default function LabelDialog({ jobId, open, onOpenChange, kind: initialKind = "Install", autoPrint = true }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState(initialKind);
  const { d, err } = useLabelData(jobId, kind, open);
  const printed = useRef(false);

  useEffect(() => {
    if (open) {
      setKind(LABEL_KINDS.includes(initialKind) ? initialKind : "Install");
      printed.current = false;
    }
  }, [open, initialKind]);

  useEffect(() => {
    if (!open || !autoPrint || !d || printed.current) return;
    printed.current = true;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [open, autoPrint, d]);

  const pdf = async () => {
    try {
      await downloadFile(`/jobs/${jobId}/install-label.pdf?kind=${kind}`,
        `${kind.toLowerCase()}-label-${d?.job_code || jobId}.pdf`);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] w-auto max-w-none overflow-y-auto border-zinc-800 bg-zinc-950 p-4"
        data-testid="label-dialog"
      >
        <style>{LABEL_PRINT_CSS}</style>
        <DialogTitle className="sr-only">{kind} label 4×6</DialogTitle>
        <div className="no-print mb-3 flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-zinc-800" data-testid="label-kind-toggle">
            {LABEL_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                data-testid={`label-kind-${k.toLowerCase()}`}
                onClick={() => setKind(k)}
                className={`h-8 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  kind === k ? "bg-indigo-500 text-white" : "text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {k}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/settings/install-label")}
              data-testid="label-settings-btn">
              <GearSix size={15} weight="bold" className="mr-1" /> Fields
            </Button>
            <Button variant="outline" size="sm" onClick={pdf} data-testid="label-pdf-btn">
              <DownloadSimple size={15} weight="bold" className="mr-1" /> PDF
            </Button>
            <Button size="sm" onClick={() => window.print()} data-testid="label-print-btn">
              <Printer size={15} weight="bold" className="mr-1" /> Print {kind} 4×6
            </Button>
          </div>
        </div>
        {err && <div className="no-print p-6 text-sm text-red-400">{err}</div>}
        {!err && !d && <div className="no-print p-6 text-sm text-zinc-500">Loading label…</div>}
        {d && <LabelSheet d={d} />}
      </DialogContent>
    </Dialog>
  );
}
